import type { FC, ReactNode } from 'react'

import { useId } from 'react'
import { cn } from '@/lib/utils'

interface LogoProps {
    tone?: 'light' | 'dark'
    className?: string
}

interface LogoMarkProps {
    tone?: 'light' | 'dark'
    className?: string
}

const TEETH = [0, 45, 90, 135]

// Mark: a car part (gear) inside a camera viewfinder, the photo-to-part idea of the app.
export const LogoMark: FC<LogoMarkProps> = ({ tone = 'dark', className }): ReactNode => {
    const gradientId = useId()
    const isLight = tone === 'light'
    const ink = isLight ? `url(#${gradientId})` : '#ffffff'

    return (
        <svg viewBox='0 0 40 40' aria-hidden='true' className={cn('shrink-0', className)}>
            <defs>
                <linearGradient id={gradientId} x1='0' y1='0' x2='1' y2='1'>
                    <stop offset='0' stopColor='#1a7cf0' />
                    <stop offset='1' stopColor='#0b3478' />
                </linearGradient>
            </defs>

            <rect width='40' height='40' rx='12' fill={isLight ? '#ffffff' : `url(#${gradientId})`} />

            <g fill='none' stroke={ink} strokeWidth='2.6' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M9 15v-4a2 2 0 0 1 2-2h4' />
                <path d='M25 9h4a2 2 0 0 1 2 2v4' />
                <path d='M31 25v4a2 2 0 0 1-2 2h-4' />
                <path d='M15 31h-4a2 2 0 0 1-2-2v-4' />
            </g>

            <g fill={ink}>
                {TEETH.map((angle) => (
                    <rect key={angle} x='18.4' y='12.6' width='3.2' height='14.8' rx='1.1' transform={`rotate(${angle} 20 20)`} />
                ))}
                <circle cx='20' cy='20' r='5.4' />
            </g>

            <circle cx='20' cy='20' r='2.1' fill={isLight ? '#ffffff' : '#0a5fd6'} />
        </svg>
    )
}

export const Logo: FC<LogoProps> = ({ tone = 'dark', className }): ReactNode => {
    const isLight = tone === 'light'

    return (
        <span className={cn('inline-flex items-center gap-2.5', className)}>
            <LogoMark
                tone={tone}
                className={cn('size-10', !isLight && 'drop-shadow-[0_8px_14px_rgba(10,106,230,0.45)]')}
            />

            <span
                className={cn(
                    'font-heading text-[1.7rem] leading-none font-extrabold tracking-[-0.04em] transition-colors duration-300',
                    isLight ? 'text-white' : 'text-foreground'
                )}
            >
                jeme
            </span>
        </span>
    )
}
